import cn from 'classnames'
import { ReactNode, useEffect, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'

import styles from './CockpitScopeChip.module.scss'

export interface ICockpitScopeChipOption {
  key: string
  label: ReactNode
}

interface ICockpitScopeChipProps {
  /** What the chip shows at rest: the current value of this scope dimension. */
  value: ReactNode
  /** Read by screen readers on the chip, and shown above the options. */
  label: ReactNode
  ariaLabel: string
  options: ICockpitScopeChipOption[]
  selectedKeys: string[]
  onSelect: (key: string) => void
  /** Periods stay open while several quarters are picked; single-choice chips close. */
  closeOnSelect?: boolean
  /** `chip` is a grey pill; `toolbar` is a segment of a white toolbar, its value naming itself; `text` is the value alone with its chevron, for a labelled row;
   *  `pill` is a blue segment in a grey track, under a caption; `segmentOnly` is that segment alone, in a
   *  track its row draws for several values; `heading` is it as a card header's title;
   *  `display` is the lead of the plan deck, larger than a title; `framed` is the value in a card
   *  header chip's hairline frame. */
  appearance?:
    | 'chip'
    | 'toolbar'
    | 'text'
    | 'pill'
    | 'segmentOnly'
    | 'heading'
    | 'display'
    | 'framed'
  /** Every value the chip can rest on. The chip is drawn as wide as the longest of them, so picking
   *  another value does not change its width or move what stands beside it. */
  sizeValues?: ReactNode[]
  /** Which edge of the trigger the list lines up with; a value at a row's right edge opens to the left. */
  popupAlign?: TPopupHorizontal
  /** `toolbar` only: a caption before the value, which then stands in a blue segment with its
   *  chevron, as the selected tab in its track. Marks a scope narrowed from its default. */
  caption?: ReactNode
  /** A line under the options, saying what picking one does. */
  footnote?: ReactNode
  tourId?: string
  disabled?: boolean
  disabledTooltip?: ReactNode
}

/** One scope dimension as a chip with its own list: the resting state reads as text, the list
 *  opens only when the value is changed. */
export const CockpitScopeChip = ({
  value,
  label,
  ariaLabel,
  options,
  selectedKeys,
  onSelect,
  closeOnSelect = true,
  appearance = 'chip',
  popupAlign = TPopupHorizontal.Left,
  caption,
  footnote,
  tourId,
  sizeValues,
  disabled = false,
  disabledTooltip,
}: ICockpitScopeChipProps) => {
  const t = useSafetyTranslation()
  const [isOpened, setOpened] = useState(false)

  useEffect(() => {
    if (disabled) setOpened(false)
  }, [disabled])
  const isCaptioned = appearance === 'toolbar' && Boolean(caption)

  /* The longer values sit in the same grid cell as the shown one, hidden, so the cell takes the
     longest one's width in whatever font and language the page is in. */
  const valueFace = sizeValues ? (
    <span className={styles.sizer}>
      <span className={styles.value}>{value}</span>
      {sizeValues.map((sizeValue, index) => (
        <span key={index} aria-hidden>
          {sizeValue}
        </span>
      ))}
    </span>
  ) : (
    <span className={styles.value}>{value}</span>
  )
  const face = (
    <>
      {valueFace}
      <i
        className={cn(
          'kbase',
          !disabled && isOpened ? 'kbase-chevron-top' : 'kbase-chevron-bottom'
        )}
        aria-hidden
      />
    </>
  )

  const trigger = (
    <button
      type="button"
      className={cn(styles.trigger, styles[appearance], {
        [styles.opened]: !disabled && isOpened,
        [styles.captioned]: isCaptioned,
      })}
      aria-label={t(ariaLabel)}
      aria-expanded={!disabled && isOpened}
      disabled={disabled}
      data-tour-id={tourId}
    >
      {isCaptioned && <span>{caption}</span>}
      {appearance === 'pill' || appearance === 'segmentOnly' || isCaptioned ? (
        <span className={styles.segment}>{face}</span>
      ) : (
        face
      )}
    </button>
  )

  if (disabled && disabledTooltip) {
    return (
      <Tooltip className={styles.root} tipContent={() => <>{disabledTooltip}</>}>
        {trigger}
      </Tooltip>
    )
  }

  return (
    <AbsolutePopup
      rootClassName={styles.root}
      popupClassName={styles.popupWrap}
      verticalStartPosition={TPopupVertical.Bottom}
      horizontalStartPosition={popupAlign}
      visibilityCallback={(visible) => {
        if (disabled) return false
        setOpened(visible)
        return visible
      }}
      popupContent={({ action }) => (
        <PopPaper shadowPosition="auto" className={styles.popup}>
          <div className={styles.options} role="listbox" aria-label={t(ariaLabel)}>
            <div className={styles.label}>{label}</div>

            {options.map((option) => {
              const isSelected = selectedKeys.includes(option.key)

              return (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={disabled}
                  className={cn(styles.option, { [styles.selected]: isSelected })}
                  onClick={() => {
                    if (disabled) return
                    onSelect(option.key)

                    if (closeOnSelect) {
                      setOpened(false)
                      action.close()
                    }
                  }}
                >
                  {option.label}
                  {isSelected && <i className="kbase kbase-check" aria-hidden />}
                </button>
              )
            })}
          </div>
          {footnote && <div className={styles.footnote}>{footnote}</div>}
        </PopPaper>
      )}
    >
      {trigger}
    </AbsolutePopup>
  )
}

CockpitScopeChip.displayName = 'CockpitScopeChip'
